import React, { useState } from 'react';
import { MessageCircle, Package } from 'lucide-react';
import { WEB3FORMS_KEY, WHATSAPP } from '../constants';

export default function ContactFormWidget({ serviceName = '', formTitle = '' }) {
  const [formData, setFormData] = useState({ name: '', phone: '', service: serviceName, size: '', date: '', botcheck: '' });
  const [formStatus, setFormStatus] = useState('idle');

  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.botcheck) return;
    setFormStatus('loading');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `Neue Anfrage – ${formData.service || 'Räumung'} Wien`,
          from_name: formData.name,
          name: formData.name,
          phone: formData.phone,
          'Was geräumt werden soll': formData.service,
          'Größe / Zimmer': formData.size || 'Nicht angegeben',
          'Wunschtermin': formData.date || 'Nicht angegeben',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setFormStatus('success');
        setFormData({ name: '', phone: '', service: serviceName, size: '', date: '', botcheck: '' });
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  if (formStatus === 'success') {
    return (
      <div className="text-center py-6" data-testid="form-success">
        {/* Animated boxes flying out */}
        <div className="flex justify-center gap-3 mb-5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="success-box" style={{ animationDelay: `${i * 0.18}s` }}>
              <Package size={30} className="text-[#E8722A]" />
            </div>
          ))}
        </div>
        <p className="cabinet font-black text-2xl text-[#7C9A82] tracking-tight mb-1">Erledigt.</p>
        <p className="font-bold text-[#1A1A2E] text-base mb-1">Wir melden uns.</p>
        <p className="text-gray-400 text-sm mb-5">Innerhalb von <strong>24 Stunden</strong> persönlich.</p>
        <div className="flex gap-2 justify-center flex-wrap">
          <a href={WHATSAPP} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-[#25D366] text-white font-bold px-4 py-2 rounded-lg text-sm">
            <MessageCircle size={14} /> WhatsApp
          </a>
          <button onClick={() => setFormStatus('idle')} className="text-[#E8722A] text-sm hover:underline px-4 py-2">
            Neue Anfrage
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="contact-form-widget">
      <input type="text" name="botcheck" value={formData.botcheck} onChange={handleChange}
        style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

      <div>
        <label className="block text-xs font-black text-[#1A1A2E]/60 mb-1 uppercase tracking-wide">
          Name <span className="text-[#E8722A]">*</span>
        </label>
        <input type="text" name="name" required value={formData.name} onChange={handleChange}
          placeholder="Max Mustermann"
          className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A1A2E] transition-colors bg-[#FAFAF8]" />
      </div>
      <div>
        <label className="block text-xs font-black text-[#1A1A2E]/60 mb-1 uppercase tracking-wide">
          Telefon <span className="text-[#E8722A]">*</span>
        </label>
        <input type="tel" name="phone" required value={formData.phone} onChange={handleChange}
          placeholder="+43 699 ..."
          className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A1A2E] transition-colors bg-[#FAFAF8]" />
      </div>
      <div>
        <label className="block text-xs font-black text-[#1A1A2E]/60 mb-1 uppercase tracking-wide">Objekt *</label>
        <select name="service" required value={formData.service} onChange={handleChange}
          className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A1A2E] bg-[#FAFAF8] transition-colors">
          <option value="" disabled>Wohnung, Keller, Dachboden …</option>
          <option>Wohnung</option>
          <option>Haus</option>
          <option>Keller</option>
          <option>Dachboden</option>
          <option>Büro</option>
          <option>Sonstiges</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-black text-[#1A1A2E]/60 mb-1 uppercase tracking-wide">Größe</label>
          <select name="size" value={formData.size} onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1A1A2E] bg-[#FAFAF8] transition-colors">
            <option value="">Optional</option>
            <option>1-Zimmer</option>
            <option>2-Zimmer</option>
            <option>3-Zimmer</option>
            <option>4+ Zimmer</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-black text-[#1A1A2E]/60 mb-1 uppercase tracking-wide">Termin</label>
          <input type="date" name="date" value={formData.date} onChange={handleChange}
            min={new Date().toISOString().split('T')[0]}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1A1A2E] transition-colors bg-[#FAFAF8]" />
        </div>
      </div>
      {formStatus === 'error' && (
        <p className="text-red-500 text-xs bg-red-50 p-2 rounded">Fehler. Bitte rufen Sie uns direkt an.</p>
      )}
      <button type="submit" disabled={formStatus === 'loading'} data-testid="contact-form-submit"
        className="w-full bg-[#E8722A] hover:bg-[#d4651e] disabled:opacity-60 text-white font-black px-4 py-4 rounded-lg transition-colors text-sm">
        {formStatus === 'loading' ? 'Wird gesendet...' : 'Kostenlos anfragen →'}
      </button>
      <p className="text-center text-xs text-gray-400">Rückruf innerhalb 24h · 100% kostenlos</p>
    </form>
  );
}
